import { useEffect } from "react";
import DataTable, { Column } from "../../components/DataTable";
import SubHeadings from "../../components/SubHeadings";
import { AddStaffForm } from "../../types/staff";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { fetchStaff, setFilters } from "../../features/staff/staffSlice";
import { useDispatch } from "react-redux";
import DownloadButton from "../../components/common/DownloadButton";
import {
  exportToExcel,
  formatRoleForDisplay,
} from "../../util/functions/UniversalFunctions";
import { useBranchCode } from "../../util/hooks/useBranch";

export default function StaffList() {
  const dispatch = useDispatch<AppDispatch>();

  const { staffList, loading, filters } = useSelector(
    (state: RootState) => state.staff,
  );

  const branchCode = useBranchCode();

  const navigate = useNavigate();

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        ...filters,
        branchCode,
        page: 0,
        size: 40,
      }),
    );
  }, [dispatch, filters, branchCode]);

  const handleViewProfile = (staffId: number) => {
    const staff = staffList?.find((s) => s.staffId === staffId);
    if (staff) {
      navigate(`/staff/staff-profile/${staffId}`, {
        state: { staffData: staff },
      });
    }
  };

  const columns: Column<AddStaffForm>[] = [
    {
      label: "SL NO.",
      accessor: "employeeCode",
      searchable: false,
    },
    {
      label: "Profile",
      accessor: "fullName",
      searchable: false,
      render: (item) => (
        <div>
          {!item?.profile_image_url ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.profile_image_url}
              alt="profile"
              className="w-10 h-10 rounded-full object-cover bg-background-200"
            />
          )}
        </div>
      ),
    },
    {
      label: "Staff Name",
      accessor: "fullName",
      searchable: true,
      render: (item) => (
        <div className="text-start">
          <div className="font-bold text-sm text-neutral-400">
            {item.fullName}
          </div>
          <div className="text-xs text-neutral-300">
            {formatRoleForDisplay(item.roleName)}
          </div>
        </div>
      ),
    },
    {
      label: "Email address/Phone number",
      accessor: "mobile",
      searchable: true,
      render: (item) => (
        <div className="text-start">
          <div className="text-neutral-400 text-[13px]">{item.email}</div>
          <div className=" text-neutral-400 text-[13px]">{item.mobile}</div>
        </div>
      ),
    },
    {
      label: "Classrooms Assigned",
      accessor: "classroomsAssigned",
      // searchable: true,
      render: (item) => {
        const classrooms = item.classroomsAssigned;

        if (!classrooms || classrooms.length === 0) {
          return (
            <div className="text-neutral-300 text-sm font-medium px-2 py-1">
              ----
            </div>
          );
        }

        return (
          <div className="flex flex-wrap gap-1 w-44">
            {classrooms.map((classroom, idx) => (
              <span
                key={idx}
                className={`px-2 py-1 text-[11px] font-medium rounded-[4px] ${
                  idx % 4 === 0
                    ? "bg-primary-50 text-primary-600"
                    : idx % 4 === 1
                      ? "bg-success-50 text-success-600"
                      : idx % 4 === 2
                        ? "bg-warning-50 text-warning-600"
                        : "bg-error-50 text-error-600"
                }`}
              >
                {classroom}
              </span>
            ))}
          </div>
        );
      },
    },

    {
      label: "Action",
      accessor: "action",
      searchable: false,
      render: (item) => (
        <button
          onClick={() => handleViewProfile(item.staffId)}
          className="px-2 py-1 text-nowrap text-xs text-warning-700 bg-warning-50 rounded-[4px] font-medium"
        >
          View Profile
        </button>
      ),
    },
  ];

  const groupedStaff =
    staffList?.reduce(
      (groups, staff) => {
        const key = staff.roleName || "Others";
        if (!groups[key]) groups[key] = [];
        groups[key].push(staff);
        return groups;
      },
      {} as Record<string, AddStaffForm[]>,
    ) || {};

  const handleSearchChange = (accessor: string, value: string) => {
    dispatch(setFilters({ [accessor]: value || "", page: 0 })); // reset page
  };

  const handleDownload = () => {
    if (!staffList || !staffList.length) return;

    exportToExcel(staffList, "Staff_List");
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end items-end">
        <DownloadButton
          onClick={handleDownload}
          disabled={loading || !staffList || staffList.length === 0}
        />
      </div>
      <div className="flex flex-col gap-2">
        {Object.entries(groupedStaff).length > 0 ? (
          Object.entries(groupedStaff).map(([designation, staffList]) => (
            <div
              key={designation}
              className="grid gap-7 bg-white px-6 py-5 rounded-lg"
            >
              {/* TODO: Temp */}
              <SubHeadings text={formatRoleForDisplay(designation)} />
              <DataTable
                columns={columns}
                data={staffList}
                searchFilters={Object.fromEntries(
                  Object.entries(filters).map(([key, value]) => [
                    key,
                    String(value),
                  ]),
                )}
                onSearchChange={handleSearchChange}
                isLoading={loading}
              />
            </div>
          ))
        ) : (
          <div className="bg-white px-6 py-5 rounded-lg">
            <SubHeadings text="Staff" />
            <DataTable
              columns={columns}
              data={[]}
              searchFilters={Object.fromEntries(
                Object.entries(filters).map(([key, value]) => [
                  key,
                  String(value),
                ]),
              )}
              onSearchChange={handleSearchChange}
              isLoading={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
